import { call, put, take, all, race, delay } from 'redux-saga/effects';
import { startPolling, stopPolling, updatePollingStats } from 'actions/polling';
import { initState } from 'reducers/polling';
import { POLL_START, POLL_STOP } from 'constants/actionTypes';

// Config object:
// {
//     asyncFetch: () => {},        -> Request. Default: () => {}
//     callback: () => true,        -> Callback when request. It must return true or false.
//     callbackEnd: () => {},       -> Callback when polling is finished.
//                                     When true polling continue and when false stop polling. Default: () => {}
//     delay: 5,                    -> Seconds between request. Default: 1.
//     ttl: 2,                      -> Time to live. Number of times to request.
//                                     If undefined, it will request until you stop polling with stopPolling action or
//                                     return false in callback.
//                                     Default: undefined.
//     retryOnFailure: true,        -> Retry when request failed. Default: false
//     retryAfter: 5,               -> Seconds to retry request when it has failed. Default: 0.
//     stopAfterRetries: 2,         -> Number of retries when request has failed. Default: 0.
// };

const hasTtl = ttl => {
    return typeof ttl !== 'undefined';
};

const checkContinuePolling = stats => {
    const { shouldContinue, ttl } = stats;
    return shouldContinue && (!hasTtl(ttl) || ttl > 0);
};

function* waitDelay(delayTime, stats) {
    for (let i = 1; i <= delayTime; ++i) {
        yield delay(1000);
        stats.nextPollEta = delay - i;
        yield put(updatePollingStats(stats));
    }
}

function* poll(action) {
    const config = {
        asyncFetch: () => {},
        callback: () => true,
        callbackEnd: () => {},
        delay: 1,
        ttl: undefined,
        retryOnFailure: false,
        retryAfter: 0,
        stopAfterRetries: 0,
        ...action.config,
    };
    const stats = {
        ...initState,
        ttl: config.ttl,
    };

    while (true) {
        stats.inProgress = true;
        stats.error = false;
        stats.ok = !stats.error;
        stats.fetching = true;
        stats.counter = stats.counter + 1;
        stats.isLastRequest = config.maxRequests && stats.counter >= config.maxRequests;

        try {
            yield put(updatePollingStats(stats));
            if (hasTtl(stats.ttl)) {
                stats.ttl = --stats.ttl;
                stats.ttlEnd = !stats.ttl;
            }

            const response = config.maxRequests
                ? yield call(config.asyncFetch, { query: { isLast: stats.isLastRequest } })
                : yield call(config.asyncFetch);
            stats.fetching = false;
            stats.nextPollEta = config.delay;
            stats.shouldContinue = checkContinuePolling(stats);
            const shouldContinueFromCallBack = yield config.callback(response, stats);
            stats.shouldContinue = stats.shouldContinue && shouldContinueFromCallBack;
            stats.shouldContinue = stats.shouldContinue && !stats.isLastRequest;
            stats.retriesAfterFailure = 0;
            stats.lastResponseStatus = 'success';
            yield put(updatePollingStats(stats));
            if (stats.shouldContinue) {
                yield waitDelay(config.delay, stats);
            } else {
                stats.inProgress = false;
                stats.fetching = false;
                yield config.callbackEnd(response, stats);
                yield put(updatePollingStats(initState));
                yield put(stopPolling());
            }
        } catch (error) {
            const { response } = error;
            const shouldRetry =
                config.retryOnFailure && !stats.ttlEnd && stats.retriesAfterFailure < config.stopAfterRetries;
            stats.fetching = false;
            stats.error = true;
            stats.ok = !stats.error;
            stats.lastResponseStatus = 'error';
            stats.nextPollEta = shouldRetry ? config.retryAfter : null;
            stats.shouldContinue = shouldRetry;
            const shouldContinueFromCallBackError = yield config.callback(response, stats);
            stats.shouldContinue = stats.shouldContinue && shouldContinueFromCallBackError;
            yield put(updatePollingStats(stats));
            if (stats.shouldContinue) {
                yield waitDelay(config.retryAfter, stats);
                ++stats.retriesAfterFailure;
                yield put(startPolling(config));
            } else {
                stats.inProgress = false;
                yield config.callbackEnd(response, stats);
                yield put(updatePollingStats(initState));
                yield put(stopPolling());
            }
        }
    }
}

function* watchPollingTasks() {
    while (true) {
        const action = yield take(POLL_START);
        yield race([call(poll, action), take(POLL_STOP)]);
    }
}

export default function* polling() {
    yield all([watchPollingTasks()]);
}
